import { createContext, useContext, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import styles from './index.module.scss'
import { Button, Checkbox, Drawer, Input, InputNumber, message, Popover, Select, Space, Table, Upload, Tooltip } from 'antd'
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons'
import PSAIModal from "@PSAIComponents/PSAIModal";
import Utils from '@utils/utils'
import BatchAdd from '../BatchAdd'
import ExcelBatchAdd from '../ExcelBatchAdd'
import API from '@api/api'
import dayjs from 'dayjs';
import Iconfont from '@utils/Iconfont';

const _ = require('lodash')

const ValidContext = createContext()

const SkuDetailForm = forwardRef((props, ref) => {
    const { readOnly } = props
    const {
        setValid, validRef
    } = useContext(ValidContext)
    // 每行数据结构：
    //   { uid, skuCode, skuImages, matchImages, refImages, imageCount, description, demands, urgent }
    //   • uid：本地生成的唯一行 ID，仅用于 React rowKey 和行内部定位，不提交给业务
    //   • 其余字段为业务字段，允许为空，validate 时根据 columns.required 校验
    const [dataSource, setDataSource] = useState(() => [createEmptyRow()])
    // 用 ref 保存最新 dataSource / columns，避免 useImperativeHandle 闭包问题
    const dataSourceRef = useRef(dataSource)
    const columnsRef = useRef([])
    const tableRef = useRef(null)
    // 对外暴露方法，供父组件通过 ref 调用
    useImperativeHandle(ref, () => ({
        /**
         * 校验并返回表格数据
         * • 校验规则：递行扫描 dataSource，出 columns 中 required:true 的列作空值校验
         * • 校验通过：返回当前 dataSource
         * • 校验失败：抛出 { errorFields: [{ rowIndex, dataIndex, message }] }，并提示第一条错误
         */
        validate: async () => {
            validRef.current = false
            setValid(validRef.current)
            const cols = columnsRef.current || []
            const data = dataSourceRef.current || []

            // 空表运
            if (data.length === 0) {
                const err = { errorFields: [{ message: '请至少添加一条SKU' }] }
                message.warning(err.errorFields[0].message)
                throw err
            }

            // 提取必填列（required: true）
            const requiredCols = cols
                .filter(c => c && c.required && c.dataIndex)
                .map(c => ({
                    dataIndex: c.dataIndex,
                    label: c.label || c.dataIndex,
                }))

            const errorFields = []
            data.forEach((row, rowIndex) => {
                const uid = row.uid
                requiredCols.forEach(({ dataIndex, label }) => {
                    if (isEmptyValue(row[dataIndex])) {

                        validRef.current = (pre => {
                            if (!pre) return { [uid]: { [dataIndex]: true } }
                            else return {
                                ...pre,
                                [uid]: {
                                    ...pre[uid],
                                    [dataIndex]: true,
                                }
                            }
                        })(validRef.current)
                        setValid(validRef.current)

                        errorFields.push({
                            uid,
                            rowIndex,
                            dataIndex,
                            message: `请补全必填项`,
                        })
                    }
                })
            })
            if (errorFields.length > 0) {
                message.warning(errorFields[0].message)
                tableRef.current?.scrollTo({ key: errorFields[0].uid })
                throw { errorFields }
            }

            // skuCode不能重复
            // 过滤出重复的sku，并根据skuCode去重
            const repeatSkuCount = new Map()
            data.forEach((row, rowIndex) => {
                const uid = row.uid
                if (repeatSkuCount.has(row.skuCode)) {
                    repeatSkuCount.set(row.skuCode, repeatSkuCount.get(row.skuCode) + 1)

                    const dataIndex = "skuCode"
                    validRef.current = (pre => {
                        if (!pre) return { [uid]: { [dataIndex]: true } }
                        else return {
                            ...pre,
                            [uid]: {
                                ...pre[uid],
                                [dataIndex]: true,
                            }
                        }
                    })(validRef.current)
                    setValid(validRef.current)

                    errorFields.push({
                        uid,
                        rowIndex,
                        dataIndex,
                        message: `商品货号不能重复`,
                    })
                } else {
                    repeatSkuCount.set(row.skuCode, 1)
                }
            })
            if (errorFields.length > 0) {
                message.warning(errorFields[0].message)
                tableRef.current?.scrollTo({ key: errorFields[0].uid })
                throw { errorFields }
            }

            return stripUid(data)
        },
        /**
         * 获取当前表格数据（不触发校验）——对外剥掉 uid
         */
        getValues: async () => stripUid(dataSourceRef.current),
        /**
         * 回显数据（父组件调用，传入业务行数组）
         * 自动为每行补 uid；传空或空数组时重置为一条空行
         */
        setValues: (rows) => {
            const normalized = normalizeRows(rows)
            setDataSource(normalized.length ? normalized : [createEmptyRow()])
        },
    }), [])
    // 服务内容列表
    const [demandTags, setDemandTags] = useState([])
    useEffect(() => {
        API.getDemandTags().then(res => {
            if (res.code != 0) throw new Error(res.message)
            setDemandTags(res.data.map(item => ({ label: item.name, value: item.id, resourceType: item.resourceType })) || []) 
        }).catch(err => {
            message.error(err.message)
            setDemandTags([])
        })
    }, [])
    // 保存行数据
    const handleRowSave = useCallback((record, dataIndex, value) => {
        setDataSource(pre => {
            const res = pre.map(item =>
                item.uid === record.uid ? { ...item, [dataIndex]: value } : item
            )
            props.onChange(res)
            return res
        })
    }, [])
    // 应用到所有行
    const handleApply = useCallback((dataIndex, value) => {
        setDataSource(pre => {
            const res = pre.map(item =>
                ({ ...item, [dataIndex]: value })
            )
            props.onChange(res)
            return res
        })
    }, [])
    // 删除行数据
    const handleRowRemove = useCallback((index) => {
        PSAIModal.confirm({
            title: '确认删除吗？',
            okText: '确认',
            okType: 'danger',
            onOk: () => {
                setDataSource(pre => {
                    const res = pre.filter((item, i) => i != index)
                    props.onChange(res)
                    return res
                })
            },
        })
    }, [])
    const columns = useMemo(() => {
        return [{
            title: <div style={{height:16,display:"flex",alignItems:"center",gap:4}}>商品货号<span style={{ color:"red",fontSize:20,lineHeight:"16px",position:"relative",top:4 }}>*</span></div>,
            label: '商品货号',
            dataIndex: 'skuCode',
            key: 'skuCode',
            editable: true,
            required: true,
        }, {
            title: <div style={{height:16,display:"flex",alignItems:"center",gap:4}}>源素材<span style={{ color:"red",fontSize:20,lineHeight:"16px",position:"relative",top:4 }}>*</span></div>,
            label: '源素材',
            dataIndex: 'skuImages',
            key: 'skuImages',
            editable: true,
            required: true,
        }, {
            title: '搭配图',
            dataIndex: 'matchImages',
            key: 'matchImages',
            editable: true,
        }, {
            title: '参考图',
            dataIndex: 'refImages',
            key: 'refImages',
            editable: true,
        }, {
            title: <div style={{height:16,display:"flex",alignItems:"center",gap:4}}>需求数量<span style={{ color:"red",fontSize:20,lineHeight:"16px",position:"relative",top:4 }}>*</span></div>,
            label: '需求数量',
            dataIndex: 'imageCount',
            key: 'imageCount',
            editable: true,
            required: true,
        }, {
            title: 'SKU需求描述',
            dataIndex: 'description',
            key: 'description',
            editable: true,
        }, {
            title: '服务项',
            dataIndex: 'demands',
            key: 'demands',
            editable: true,
        }, {
            title: (
                <div style={{height:16,display:"flex",alignItems:"center",gap:4}}>
                    <span>是否加急</span>
                    <Popover
                        arrow={false}
                        content="勾选【加急】后，会优先制作、交付该商品相关内容"
                    >
                        <Iconfont type="icon-info" style={{ fontSize: 14 }} />
                    </Popover>
                </div>
            ),
            dataIndex: 'urgent',
            key: 'urgent',
            editable: true,
        }, {
            title: '操作',
            key: 'action',
            width: 60,
            fixed: 'right',
            hidden: readOnly,
            render: (_, record, index) => (
                <Iconfont type="icon-delete" style={{ fontSize: 16, color: "#808080" }} onClick={() => handleRowRemove(index)} />
            )
        }].map(col => {
            if (!col.editable) return col
            return {
                ...col,
                onCell: record => ({
                    record,
                    dataIndex: col.dataIndex,
                    editable: col.editable,
                    readOnly,
                    demandTags,
                    handleRowSave,
                    handleApply,
                })
            }
        })
    }, [readOnly, demandTags, handleRowSave, handleApply, handleRowRemove])

    // 同步 columns 到 ref，供 validate 读取最新配置
    useEffect(() => {
        columnsRef.current = columns
    }, [columns])
    useEffect(() => {
        dataSourceRef.current = dataSource
    }, [dataSource, props.onChange])

    // 批量录入
    const [batchOpen, setBatchOpen] = useState(false)
    const [excelBatchOpen, setExcelBatchOpen] = useState(false)
    const handleBatchOk = useCallback((skuList) => {
        // 批量读入后追加到现有表格（保留已有行）
        if (Array.isArray(skuList) && skuList.length > 0) {
            setDataSource(pre => {
                const existing = pre.filter(row => !isRowAllEmpty(row))
                const res = [
                    ...existing, 
                    ...normalizeRows(skuList).map(item => ({...item, imageCount: 1}))
                ]
                props.onChange(res)
                return res
            })
        }
        setBatchOpen(false)
        setExcelBatchOpen(false)
    }, [])
    return (
        <div className={styles.SkuDetail}>
            <div className={styles.SkuDetail_header}>
                <div className={styles.SkuDetail_header_title}>
                    <span>SKU详情</span>
                    <span>共1组</span>
                </div>
                <div className={styles.SkuDetail_header_buttons}>
                {
                    readOnly ? null :
                    <>
                    <Button onClick={() => setBatchOpen(true)}>文件夹批量录入</Button>
                    <Button onClick={() => setExcelBatchOpen(true)}>Excel批量录入</Button>
                    </>
                }
                </div>
            </div>
            <div className={styles.SkuDetail_table}>
                <Table
                    ref={tableRef}
                    className="customTable"
                    rowKey="uid"
                    columns={columns}
                    pagination={false}
                    dataSource={dataSource}
                    scroll={{
                        x: 'max-content',
                        y: `max-content`,
                    }}
                    components={{
                        body: {
                            cell: EditableCell,
                        }
                    }}
                />
            </div>
            <div className={styles.SkuDetail_addMore}>
            {
                readOnly ?
                null :
                <Button
                    type="dashed"
                    shape="round"
                    icon={<Iconfont type="icon-new" style={{ fontSize: 20, verticalAlign: "top" }} />}
                    onClick={() => {
                        setDataSource(pre => {
                            const res = [...pre, createEmptyRow()]
                            props.onChange(res)
                            return res
                        })
                    }}
                >
                    新增SKU
                </Button>
            }
            </div>
            <Drawer
                size={"100%"}
                title={'文件夹录入'}
                open={batchOpen}
                destroyOnHidden
                onClose={() => setBatchOpen(false)}
            >
                <BatchAdd
                    onOk={handleBatchOk}
                    onCancel={() => setBatchOpen(false)}
                />
            </Drawer>
            <Drawer
                size={"100%"}
                title={'Excel录入'}
                open={excelBatchOpen}
                destroyOnHidden
                onClose={() => setExcelBatchOpen(false)}
            >
                <ExcelBatchAdd
                    onOk={handleBatchOk}
                    onCancel={() => setExcelBatchOpen(false)}
                />
            </Drawer>
        </div>
    )
})

export default forwardRef((props, ref) => {
    const [valid, setValid] = useState(false)
    const validRef = useRef(false)
    return (
        <ValidContext.Provider value={{valid, setValid, validRef}}>
            <SkuDetailForm {...props} ref={ref} />
        </ValidContext.Provider>
    )
})

/**
 * 生成本地行唯一标识
 * 时间戳 + 随机串，保证同一毫秒批量创建也不会重复
 */
function genRowUid() {
    return `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

/**
 * 构造一条空行（只带 uid）
 */
function createEmptyRow() {
    return { uid: genRowUid(), imageCount: 1 }
}

/**
 * 统一批给数据行补 uid（已有则保留），用于回显或批量录入
 */
function normalizeRows(rows) {
    if (!Array.isArray(rows)) return []
    return rows.map(row => (
        row && row.uid ? row : { ...(row || {}), uid: genRowUid() }
    ))
}

/**
 * 对外输出时剥掉 uid，仅保留业务字段
 */
function stripUid(rows) {
    if (!Array.isArray(rows)) return []
    return rows.map(({ uid, ...rest }) => rest)
}

/**
 * 判断一行是否全空（只有 uid、无任何业务字段）
 * 用于批量录入时过滤掉原先的占位空行
 */
function isRowAllEmpty(row) {
    if (!row) return true
    return Object.keys(row).every(k => k === 'uid' || isEmptyValue(row[k]))
}

/**
 * 空值判定：null / undefined / 纯空白字符串 / 空数组 都视为空
 */
function isEmptyValue(v) {
    if (v === null || v === undefined) return true
    if (typeof v === 'string' && v.trim() === '') return true
    if (Array.isArray(v) && v.length === 0) return true
    return false
}

function EditableCell (props) {
    const {
        dataIndex,
        children,
        record,
        editable,
        readOnly,
        demandTags,
        handleRowSave,
        handleApply,
        ...restProps
    } = props
    const {
        valid, setValid, validRef
    } = useContext(ValidContext)
    // 输入框值
    const [value, setValue] = useState(record ? record[dataIndex] : null)
    const [status, setStatus] = useState(null)
    useEffect(() => {
        if (!valid) {
            setStatus(null)
            return
        }
        if (!record) {
            setStatus(null)
            return
        }
        const uid = record?.uid
        if (!valid[uid]) {
            setStatus(null)
            return
        }
        setStatus(valid?.[uid]?.[dataIndex] ? "error" : null)
    }, [record, valid, dataIndex])
    useEffect(() => {
        setValue(dataIndex ? record[dataIndex] : null)
    }, [record, dataIndex])
    // 处理输入框变化
    const handleChange = useCallback((value) => {
        setValue(value)
        handleRowSave(record, dataIndex, value)
        // 校验通过后，更新校验状态
        const uid = record.uid
        if (!validRef.current) return
        if (!validRef.current?.[uid]?.[dataIndex]) return
        validRef.current[uid][dataIndex] = false
        setValid(validRef.current)
    }, [record, handleRowSave])
    const handleApplyToAll = useCallback((value) => {
        setValue(value)
        handleApply(dataIndex, value)
        message.success("已应用服务项到全部SKU")
    }, [handleApply])

    let childNode = children
    if (editable) {
        if (dataIndex == 'skuCode') {
            childNode = (
                <Input
                    disabled={readOnly}
                    style={{width: 160}}
                    placeholder="请填写商品货号"
                    value={value}
                    onChange={e => handleChange(e.target.value.replace(/[\/\\:*?"<>|]/g, ''))}
                    status={status}
                />
            )
        } else if (dataIndex == 'skuImages') {
            childNode = (
                <UploadImages
                    disabled={readOnly}
                    value={value}
                    onChange={handleChange}
                    status={status}
                />
            )
        } else if (dataIndex == 'matchImages') {
            childNode = (
                <UploadImages
                    disabled={readOnly}
                    value={value}
                    onChange={handleChange}
                    status={status}
                />
            )
        } else if (dataIndex == 'refImages') {
            childNode = (
                <UploadImages
                    disabled={readOnly}
                    value={value}
                    onChange={handleChange}
                    status={status}
                />
            )
        } else if (dataIndex == 'imageCount') {
            childNode = (
                <InputNumber 
                    disabled={readOnly}
                    mode="spinner"
                    style={{width: 130}}
                    min={1}
                    max={1000}
                    value={value}
                    onChange={handleChange}
                    status={status}
                />
            )
        } else if (dataIndex == 'description') {
            childNode = (
                <Input.TextArea
                    disabled={readOnly}
                    style={{width: 320, height: 96}}
                    placeholder="请填写针对当前SKU的具体要求，例如特定模特动作、色彩偏好等"
                    autoSize={{ minRows: 4, maxRows: 4 }}
                    value={value}
                    onChange={e => handleChange(e.target.value)}
                />
            )
        } else if (dataIndex == 'demands') {
            // 对demandTags根据label属性进行排序，优先显示priorityTags中的标签
            const priorityTags = ['AI试衣','AI换模特','AI模拍换背景','AI制片','AI主图视频','AI试鞋']
            // Sort demandTagsOptions by priorityTags order
            const sortedDemandTagsOptions = demandTags.sort((a, b) => {
                const indexA = priorityTags.indexOf(a.label)
                const indexB = priorityTags.indexOf(b.label)
                // Items in priorityTags come first (sorted by their priority order)
                // Items not in priorityTags come after
                if (indexA === -1 && indexB === -1) return 0 // both not in priority list
                if (indexA === -1) return 1 // a not in priority list, b is
                if (indexB === -1) return -1 // b not in priority list, a is
                return indexA - indexB // both in priority list, sort by priority order
            })
            childNode = (
                <div style={{display:"flex",justifyContent:"flex-start",alignItems:"center",gap: 8}}>
                    <Select
                        disabled={readOnly}
                        style={{width: 216}}
                        maxTagCount="responsive"
                        maxTagPlaceholder={omittedValues => (
                            <Tooltip
                                styles={{ root: { pointerEvents: 'none' } }}
                                title={omittedValues.map(({ label }) => label).join(', ')}
                            >
                                <span>...</span>
                            </Tooltip>
                        )}
                        mode="multiple"
                        allowClear
                        placeholder="请选择服务内容"
                        options={sortedDemandTagsOptions}
                        value={value}
                        showSearch={{optionFilterProp: "label"}}
                        onChange={handleChange}
                        // optionRender={opt => {
                        //     const label = opt.label
                        //     const resourceType = opt.data.resourceType == 0 ? '图片' : '视频'
                        //     return <div style={{width:"100%",overflow:"hidden",display:"flex",gap:4,justifyContent:"flex-start",alignItems:"center"}}>
                        //         <span style={{flex:"none",maxWidth: "80%",overflow: "hidden",textOverflow: "ellipsis",whiteSpace: "nowrap"}}>{label}</span>
                        //         <span style={{flex:1,overflow: "hidden",textOverflow: "ellipsis",whiteSpace: "nowrap",color: '#999'}}>{resourceType}</span>
                        //     </div>
                        // }}
                    />
                    <Popover
                        content="一键应用服务项到全部SKU"
                        placement="bottom"
                        arrow={false}
                    >
                        <Iconfont
                            type="icon-apply"
                            style={value?.length == 0 ? {fontSize: 18, color: "rgba(128, 128, 128, 0.5)",cursor:"not-allowed"} : {fontSize: 18, color: "#808080",cursor:"pointer"}}
                            onClick={value?.length == 0 ? null : () => handleApplyToAll(value)}
                        />
                    </Popover>
                </div>
            )
        } else if (dataIndex == 'urgent') {
            const activedStyles = {
                root: {
                    padding: "12px 16px",
                    border: "1px solid #FF8C3F",
                    borderRadius: 8,
                    backgroundColor: "#FF8C3F",
                },
                label: {
                    color: "#FFFFFF",
                },
                icon: {
                    backgroundColor: "rgba(250, 252, 254, 0.3)",
                    borderColor: "rgba(250, 252, 254, 0.3)",
                    color: "#FFFFFF",
                }
            }
            const unactivedStyles = {
                root: {
                    padding: "12px 16px",
                    border: "1px solid #DEE2E8",
                    borderRadius: 8,
                    backgroundColor: "#FFFFFF",
                },
                label: {
                    color: "#1D1B1B",
                },
                icon: {
                    borderColor: "#DEE2E8",
                }
            }
            const handleCheck = (e) => {
                const checked = e.target.checked
                handleChange(checked)                
                // if (checked) {
                //     const notPromptKey = 'urgent_not_prompt'
                //     const notPromptExpired = localStorage.getItem(notPromptKey)
                //     if (notPromptExpired && dayjs().isBefore(dayjs(notPromptExpired))) {
                //         handleChange(checked)
                //         return
                //     }
                //     // 勾选不再提示后当前自然日不再弹出
                //     let notPrompt = false
                //     PSAIModal.info({
                //         content: (
                //             <div style={{display:"flex",flexDirection:"column",justifyContent:"flex-start", alignItems:"flex-start", gap: 16}}>
                //                 <span>选择加急后，将收取额外费用</span>
                //                 <Checkbox onChange={e => notPrompt = e.target.checked}>不再提示</Checkbox>
                //             </div>
                //         ),
                //         okText: "我已知晓",
                //         onOk: () => {
                //             handleChange(checked)
                //             if (notPrompt) {
                //                 localStorage.setItem(notPromptKey, dayjs().endOf('day').toString())
                //                 return
                //             }
                //         }
                //     })
                // } else {
                //     handleChange(checked)
                // }
            }
            childNode = (
                <Checkbox
                    disabled={readOnly}
                    checked={value}
                    onChange={handleCheck}
                    styles={value ? activedStyles : unactivedStyles}
                >
                    加急
                </Checkbox>
            )
        }
    }
    return <td {...restProps}>{childNode}</td>;
}

function UploadImages (props) {
    const {
        disabled,
        value,
        onChange,
        status,
    } = props
    const [loading, setLoading] = useState(false)
    const doUpload = useCallback(async file => {
        const name = removeImageExtension(file.name)
        const url = await Utils.uploadFile(file, true)
        if (!url) return null
        return {
            name: name,
            url: url
        }
    }, [])
    const uploadDebounce = useCallback(_.debounce(async (file, fileList) => {
        try {
            setLoading(true)
            const promiseList = []
            for (let file of fileList) {
                const isNromal = checkImageIsNormal(file)
                if (!isNromal) continue
                promiseList.push(doUpload(file))
            }
            const res = await Promise.all(promiseList)
            const succRes = res.filter(item => item)
            if (value) {
                onChange([...value, ...succRes])
            } else {
                onChange(succRes)
            }
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }, 100), [value, doUpload])
    const onUpload = useCallback(async (file, fileList) => {
        if (loading) return false
        uploadDebounce(file, fileList)
        return false;
    }, [loading, uploadDebounce])
    const onDrop = useCallback(async (e) => {
        if (loading) return false
        if ("getFilesAndDirectories" in e.dataTransfer) {
            const filesAndDirs = await e.dataTransfer.getFilesAndDirectories()
            const fileList = await Utils.getFilesFromDirs(filesAndDirs, "/")
            console.log('===handleDrop===')
            uploadDebounce(fileList[0], fileList)
            return false
        }
    }, [loading, uploadDebounce])

    const [open, setOpen] = useState(false)

    return (
        <div className={styles.UploadImages}>
        {
            (!value || value.length == 0) ? null :
            <Popover
                placement="bottomLeft"
                arrow={false}
                content={(
                    <div className={styles.PopoverContent}>
                    {value.map(item => (
                        <div key={item.url}>
                            <img src={Utils.getImgThumbnail(item.url)} alt="" fallback={item.url} />
                            <div className={styles.DeleteIcon}>
                                <Iconfont type="icon-delete" style={{ fontSize: 20 }} onClick={() => onChange(value.filter(img => img.url !== item.url))} />
                            </div>
                        </div>
                    ))}
                    </div>
                )}
                open={open && value.length > 1}
                onOpenChange={setOpen}
            >
                <div className={styles.UploadImages_imageList}>
                {(() => {
                    const firstImage = value[0]
                    const imageCount = value.length
                    return (
                        <>
                        <img src={Utils.getImgThumbnail(firstImage.url)} alt="" fallback={firstImage.url} />
                        {imageCount > 1 ? <span>共{imageCount}张</span> : null}
                        {value.length > 1 ? null : 
                        <div className={styles.DeleteIcon}>
                            <Iconfont type="icon-delete" style={{ fontSize: 20 }} onClick={() => onChange([])} />
                        </div>}
                        </>
                    )
                })()}
                </div>
            </Popover>
        } {
            disabled ? null :
            <Upload.Dragger
                classNames={{trigger: styles.UploadImages_trigger}}
                styles={{trigger: status ? {borderColor: "red"} : null}}
                multiple
                accept='image/jpeg,image/png,image/webp,image/avif'
                listType="picture-card"
                showUploadList={false}
                beforeUpload={onUpload}
                onDrop={onDrop}
            >
                <div className={styles.UploadImages_triggerContent} style={status ? {color: "red"} : null}>
                {
                    loading ?
                    <LoadingOutlined /> :
                    <PlusOutlined />
                }
                </div>
            </Upload.Dragger>
        }
        </div>
    )
}

// 检查图片，50M限制，不再限制图片尺寸不小于500*500，长宽比不超过2:1
function checkImageIsNormal(file) {
    const MAX_IMAGE_SIZE = 50 * 1024 * 1024
    try {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp' || file.type === 'image/avif';
        if (!isJpgOrPng) throw new Error('您只能上传JPG/PNG/WEBP/AVIF格式的文件，请上传到参考文件中')
        if (file.size > MAX_IMAGE_SIZE) throw new Error(`图片最大支持${MAX_IMAGE_SIZE / 1024 / 1024}MB，请压缩体积后上传`)
        return true
    } catch (err) {
        message.warning(err.message)
        return false
    }
}

function removeImageExtension(fileName, accept = "image/png, image/jpeg, image/webp, image/avif") {
    if (!fileName || fileName == 'undefined') return '未命名';
    let allowedExtensions = accept.split(',')
        .map(type => type.trim().split('/')[1])
        .filter(ext => ext);

    if (allowedExtensions.includes("jpeg") && !allowedExtensions.includes("jpg")) {
        allowedExtensions.push("jpg");
    }

    const lastDotIndex = fileName.lastIndexOf('.');

    if (lastDotIndex <= 0) return fileName;


    const fileExtension = fileName.slice(lastDotIndex + 1).toLowerCase();


    if (allowedExtensions.includes(fileExtension)) {
        return fileName.slice(0, lastDotIndex);
    }

    return fileName;
}